import { useEffect, useState } from 'react'
import { Logo } from './Logo'
import { BOOKING_TYPES, openBooking, penzionUrlFrom } from '../lib/booking'
import { useWebContent } from '../lib/webPrices'

type NavChild = { label: string; href?: string; book?: string }   // book = klíč v BOOKING_TYPES
type NavItem = {
  label: string
  href: string
  children?: NavChild[]
}

const NAV: NavItem[] = [
  {
    label: 'Ubytování',
    href: '#ubytovani',
    // Klik na typ = rovnou rezervační okno (Robin 24. 10.: „jinak ty odkazy nemají smysl")
    // Robin 5. 11. 2026: Ubytování = penzion; chatky, Tiny House, bivak a karavan patří pod Rybolov
    children: [
      { label: 'Pokoj penzionu', book: 'penzion' },
      { label: 'Přehled ubytování', href: '#ubytovani' },
    ],
  },
  {
    label: 'Rybolov',
    href: '#rybolov',
    children: [
      { label: 'Rybářská chatka', book: 'chatka' },
      { label: 'Tiny House', book: 'glamping_tiny' },
      { label: 'Bivak', book: 'kemp' },
      { label: 'Karavan', book: 'karavan' },
      { label: 'Přehled rybolovu', href: '#rybolov' },
      { label: 'O rybolovu u nás', href: '#volny-cas-rybolov' },
      { label: 'Pravidla rybolovu', href: '#pravidla-rybolov' },
    ],
  },
  {
    label: 'Sport',
    href: '#volny-cas-sport',
    children: [
      { label: 'Beach volejbal', book: 'beach' },
      { label: 'Tenisový kurt', book: 'tenis' },
      { label: 'S rodinou', href: '#volny-cas-rodina' },
      { label: 'Restaurace & kuchyně', href: '#volny-cas-restaurace' },
    ],
  },
  { label: 'Týdenní menu', href: '#menu' },
  {
    label: 'Akce & nabídky',
    href: '#nabidky',
    children: [
      { label: 'Catering', href: '#nabidky-catering' },
      { label: 'Dárkové vouchery', href: '#koupit-voucher' },
    ],
  },
  { label: 'Galerie', href: '#galerie' },
  {
    label: 'Důležité info',
    href: '#info-cenik',   // klik na položku = první podstránka (ceník), ne náhodně FAQ
    children: [
      { label: 'Celkový ceník', href: '#info-cenik' },
      { label: 'Ubytovací řád', href: '#info-rad' },
      { label: 'Časté dotazy', href: '#info-faq' },
      { label: 'Příjezd · odjezd', href: '#info-prijezd' },
      { label: 'Obchodní podmínky', href: '#podminky' },
      { label: 'Platební a dodací podmínky', href: '#info-platba' },
      { label: 'Reklamační řád', href: '#info-reklamace' },
      { label: 'Pravidla rezervace · sport', href: '#pravidla' },
      { label: 'Pravidla rybolovu', href: '#pravidla-rybolov' },
      { label: 'Ochrana osobních údajů', href: '#gdpr' },
      { label: 'Kontakt', href: '#kontakt' },
    ],
  },
]

function Chevron() {
  return (
    <svg viewBox="0 0 24 24" className="w-3 h-3 opacity-50" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

export function Header() {
  const content = useWebContent()
  const penzionUrl = penzionUrlFrom(content as Record<string, unknown>)   // penzion dočasně přes Booking.com
  const [scrolled, setScrolled] = useState(false)
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30)
    onScroll()
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={`sticky top-0 inset-x-0 z-50 bg-white transition-shadow border-b border-ink-900/[0.07] ${
        scrolled ? 'shadow-[0_8px_30px_-18px_rgba(24,22,19,0.35)]' : ''
      }`}
    >
      <div className="container-x flex items-center justify-between gap-6 py-3 md:py-4">
        <a href="#top" className="flex items-center shrink-0">
          <Logo
            variant="dark"
            className={`transition-all ${
              scrolled
                ? 'h-12 md:h-14 text-[13px] md:text-[15px]'
                : 'h-14 md:h-16 xl:h-20 text-[14px] md:text-[16px] xl:text-[18px]'
            }`}
          />
        </a>

        {/* Desktop nav — 7 položek (přibyl Rybolov): na lg (1024–1279) těsnější mezery a menší písmo,
            aby lišta nepřetekla; plná velikost od xl */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
          {NAV.map((item, i) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && setOpenIdx(i)}
              onMouseLeave={() => setOpenIdx(null)}
            >
              <a
                href={item.href}
                className="text-ink-800 hover:text-brand-600 font-semibold text-[14px] xl:text-[15px] whitespace-nowrap transition py-2 flex items-center gap-1.5"
              >
                {item.label}
                {item.children && <Chevron />}
              </a>
              {item.children && openIdx === i && (
                <div className="absolute top-full left-0 mt-0 bg-white rounded-xl shadow-[0_24px_60px_-20px_rgba(24,22,19,0.3)] ring-1 ring-ink-900/[0.07] min-w-[240px] py-2 z-50">
                  {item.children.map((c) => c.book ? (
                    <button
                      key={c.label}
                      type="button"
                      onClick={() => { setOpenIdx(null); openBooking(BOOKING_TYPES[c.book!]) }}
                      className="w-full text-left px-5 py-2.5 text-ink-800 hover:bg-paper hover:text-brand-600 text-[14px] font-medium transition flex items-center justify-between gap-3"
                    >
                      {c.label}
                      <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-500">{c.book === 'penzion' && penzionUrl ? 'Booking.com ↗' : 'Rezervovat'}</span>
                    </button>
                  ) : (
                    <a
                      key={c.label}
                      href={c.href}
                      className="block px-5 py-2.5 text-ink-800 hover:bg-paper hover:text-brand-600 text-[14px] font-medium transition"
                    >
                      {c.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          {/* Partner login — vpravo, oddělený */}
          <a
            href="#partner"
            className="ml-1 pl-3 xl:pl-5 border-l border-ink-900/10 text-ink-700 hover:text-brand-600 font-semibold text-[14px] transition flex items-center gap-1.5 whitespace-nowrap"
            title="Partner login"
            aria-label="Partner login"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 xl:w-3.5 xl:h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            {/* na lg jen ikona zámku (7 položek menu), text od xl */}
            <span className="hidden xl:inline">Partner login</span>
          </a>
        </nav>

        <button
          onClick={() => setMobileOpen((o) => !o)}
          className="lg:hidden w-10 h-10 grid place-items-center rounded-lg border border-ink-900/15 text-ink-900"
          aria-label="Menu"
        >
          {mobileOpen ? (
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          )}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-ink-900/10 max-h-[75vh] overflow-y-auto">
          <div className="container-x py-2">
            {NAV.map((item) => (
              <div key={item.label} className="border-b border-ink-900/5 last:border-0">
                <a
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 text-ink-900 font-semibold text-[15px]"
                >
                  {item.label}
                </a>
                {item.children && (
                  <div className="pb-2">
                    {item.children.map((c) => c.book ? (
                      <button
                        key={c.label}
                        type="button"
                        onClick={() => { setMobileOpen(false); openBooking(BOOKING_TYPES[c.book!]) }}
                        className="w-full text-left py-1.5 pl-4 text-ink-700 text-sm flex items-center justify-between pr-2"
                      >
                        {c.label}
                        <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-500">{c.book === 'penzion' && penzionUrl ? 'Booking.com ↗' : 'Rezervovat'}</span>
                      </button>
                    ) : (
                      <a
                        key={c.label}
                        href={c.href}
                        onClick={() => setMobileOpen(false)}
                        className="block py-1.5 pl-4 text-ink-700 text-sm"
                      >
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href="#partner"
              onClick={() => setMobileOpen(false)}
              className="block py-3 text-ink-700 font-semibold text-[15px]"
            >
              Partner login
            </a>
            <a
              href="#rezervace"
              onClick={() => setMobileOpen(false)}
              className="block w-full text-center bg-brand-500 hover:bg-brand-600 text-white py-3 font-semibold text-[15px] rounded-xl mt-3 mb-3 transition-colors"
            >
              Rezervovat pobyt
            </a>
          </div>
        </div>
      )}
    </header>
  )
}
